import React, { useCallback, useEffect, useState } from 'react';
import {
    Button,
    Card,
    Descriptions,
    Drawer,
    Form,
    Input,
    InputNumber,
    Modal,
    Space,
    Table,
    Tag,
    message,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import PageHeader from '../../components/business/PageHeader';
import PermissionGuard from '../../components/business/PermissionGuard';
import RequestState from '../../components/business/RequestState';
import StatusTag from '../../components/business/StatusTag';
import { PageResult, Person } from '../../models/v2';
import { V2ApiError, v2Api } from '../../services/v2';
import { asV2Error, formatDateTime } from '../../utils/v2';

const emptyPage: PageResult<Person> = { items: [], pageNo: 1, pageSize: 20, total: 0 };

const PersonsPage = () => {
    const [form] = Form.useForm();
    const [data, setData] = useState(emptyPage);
    const [filters, setFilters] = useState<any>({ pageNo: 1, pageSize: 20 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<V2ApiError | null>(null);
    const [detail, setDetail] = useState<Person | null>(null);
    const [createVisible, setCreateVisible] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await v2Api.persons(filters));
        } catch (e) {
            setError(asV2Error(e));
        } finally {
            setLoading(false);
        }
    }, [filters]);
    useEffect(() => {
        load();
    }, [load]);
    const openDetail = async (id: number) => {
        try {
            setDetail(await v2Api.person(id));
        } catch (e) {
            message.error(asV2Error(e).message);
        }
    };
    useEffect(() => {
        const open = new URLSearchParams(window.location.hash.split('?')[1] || '').get('open');
        if (open) openDetail(Number(open));
    }, []);
    const create = async () => {
        const values = await form.validateFields();
        setSaving(true);
        try {
            const result = await v2Api.createPerson(values);
            message.success('联系人已创建');
            setCreateVisible(false);
            form.resetFields();
            load();
            openDetail(result.id);
        } catch (e) {
            message.error(asV2Error(e).message);
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        {
            title: '联系人',
            dataIndex: 'name',
            width: 160,
            render: (v: string, row: Person) => (
                <Button type="link" className="v2-link-strong" onClick={() => openDetail(row.id)}>
                    {row.preferredName || v}
                    <span className="v2-subline">ID: {row.id}</span>
                </Button>
            ),
        },
        {
            title: '任职关系',
            dataIndex: 'companyName',
            width: 220,
            render: (value: string, row: Person) => (
                <div>
                    {value || <span className="v2-muted">暂未关联企业</span>}
                    <div className="v2-muted">
                        {[row.department, row.positionTitle].filter(Boolean).join(' · ') ||
                            '部门职位待完善'}
                    </div>
                </div>
            ),
        },
        {
            title: '营销限制',
            key: 'marketingBlocked',
            width: 130,
            render: (_: any, row: Person) =>
                row.marketingBlocked ? (
                    <Tag color="red">已限制</Tag>
                ) : (
                    <Tag color="green">未限制</Tag>
                ),
        },
        {
            title: '联系方式',
            dataIndex: 'maskedValue',
            width: 170,
            render: () => <span className="v2-muted">仅在详情按权限展示</span>,
        },
        {
            title: '更新时间',
            dataIndex: 'updatedAt',
            width: 170,
            render: formatDateTime,
        },
        {
            title: '状态',
            dataIndex: 'masterStatus',
            width: 100,
            render: (v: string) => <StatusTag value={v} />,
        },
    ];

    return (
        <div className="v2-page">
            <PageHeader
                eyebrow="客户 · 自然人主档"
                title="联系人"
                description="一行代表一个自然人；任职关系、联系方式和营销许可分别维护。列表只展示脱敏联系方式。"
                extra={
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={load}>
                            刷新
                        </Button>
                        <PermissionGuard anyOf={['CUSTOMER_COMPANY_EDIT']}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setCreateVisible(true)}
                            >
                                新建联系人
                            </Button>
                        </PermissionGuard>
                    </Space>
                }
            />
            <Card className="v2-filter-card">
                <Space className="v2-wrap-space">
                    <Input.Search
                        allowClear
                        placeholder="姓名 / 企业 / 职位"
                        style={{ width: 280 }}
                        onSearch={(keyword) => setFilters({ ...filters, keyword, pageNo: 1 })}
                    />
                    <InputNumber
                        min={1}
                        placeholder="企业 ID"
                        onChange={(companyId) => setFilters({ ...filters, companyId, pageNo: 1 })}
                    />
                </Space>
            </Card>
            <Card className="v2-panel" bordered={false}>
                <RequestState
                    loading={loading}
                    error={error}
                    empty={!data.items.length}
                    onRetry={load}
                >
                    <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={data.items}
                        scroll={{ x: 980 }}
                        pagination={{
                            current: data.pageNo,
                            pageSize: data.pageSize,
                            total: data.total,
                            showSizeChanger: true,
                            onChange: (pageNo, pageSize) =>
                                setFilters({ ...filters, pageNo, pageSize }),
                        }}
                    />
                </RequestState>
            </Card>
            <Drawer
                width={720}
                title={detail ? detail.preferredName || detail.name : '联系人详情'}
                visible={!!detail}
                onClose={() => setDetail(null)}
                destroyOnClose
            >
                {detail && (
                    <>
                        <Descriptions bordered size="small" column={2}>
                            <Descriptions.Item label="姓名">{detail.name}</Descriptions.Item>
                            <Descriptions.Item label="常用称呼">
                                {detail.preferredName || '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="主档状态">
                                <StatusTag value={detail.masterStatus} />
                            </Descriptions.Item>
                            <Descriptions.Item label="版本">
                                v{detail.version || 0}
                            </Descriptions.Item>
                        </Descriptions>
                        {Array.isArray(detail.employments) && (
                            <Card className="v2-section" title="任职关系" size="small">
                                {detail.employments.map((item: any) => (
                                    <div className="v2-related-row" key={item.id}>
                                        <strong>
                                            {item.companyName || `企业 #${item.companyId}`}
                                        </strong>
                                        <span>
                                            任职关系 ID: {item.id} ·{' '}
                                            {[item.department, item.positionTitle]
                                                .filter(Boolean)
                                                .join(' · ') || '职位待完善'}
                                        </span>
                                        <StatusTag value={item.employmentStatus} />
                                    </div>
                                ))}
                            </Card>
                        )}
                        {Array.isArray(detail.contactMethods) && (
                            <Card className="v2-section" title="联系方式（已脱敏）" size="small">
                                {detail.contactMethods.map((item: any) => (
                                    <div className="v2-related-row" key={item.id}>
                                        <strong>{item.methodType}</strong>
                                        <span>{item.maskedValue}</span>
                                        <StatusTag value={item.validityStatus} />
                                    </div>
                                ))}
                            </Card>
                        )}
                    </>
                )}
            </Drawer>
            <Modal
                title="联系人快速建档"
                visible={createVisible}
                onOk={create}
                confirmLoading={saving}
                onCancel={() => setCreateVisible(false)}
                okText="创建"
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="姓名"
                        name="name"
                        rules={[{ required: true, whitespace: true, message: '请输入姓名' }]}
                    >
                        <Input maxLength={100} />
                    </Form.Item>
                    <Form.Item label="常用称呼" name="preferredName">
                        <Input maxLength={100} />
                    </Form.Item>
                    <div className="v2-form-tip">
                        从全局联系人页面创建时允许暂不关联企业，系统会标记为资料待完善。
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default PersonsPage;
